import { ref, computed } from 'vue'
import axios from '@/plugins/axios'
import Swal from 'sweetalert2'

// ═══════════════════════════════════════════════════════════════
// SINGLETON — todo vive FUERA de la función, sobrevive cambios
// de pestaña porque no depende del ciclo de vida de ningún componente
// ═══════════════════════════════════════════════════════════════

const dialogVisible  = ref(false)
const minimizado     = ref(false)
const cancelado         = ref(false)
const abortController   = ref(null)
const tocIdArchivoActual = ref(0)

// Config de la carpeta donde se importa
const tocId          = ref(0)
const nombreCarpeta  = ref('')

// Listas
const plantillas     = ref([])
const volumenes      = ref([])

// Selección
const plantillaSeleccionada = ref(null)
const volumenSeleccionado   = ref(null)
const plantillaBloqueada    = ref(false)
const archivosSeleccionados = ref([])
const archivosTabla         = ref([])  // [{ name, size, status, file }]

// Progreso
const subiendo      = ref(false)
const progreso      = ref(0)
const progressSize  = ref('0 b')
const completado    = ref(false)
const errorSubida   = ref(null)

// OCR / AV
const tieneArchivoAV     = ref(false)
const configOCRActiva    = ref(false)
const idiomaSeleccionado = ref('es')

// Cuando el upload termina, guardamos el tocId para que treeView pueda recargar
const pendingReloadTocId = ref(null)

const extensionesAV       = ['mp3', 'mp4', 'wav', 'm4a', 'mkv', 'avi', 'mov']
const formatosPermitidos  = ['mp4','mkv','webm','mov','avi','m4v','mp3','wav','flac',
                             'm4a','pdf','png','tiff','tif','jpg','jpeg','zip','rar','doc','docx']

// ── helpers ──────────────────────────────────────────────────
const _formatFileSize = (bytes) => {
  if (!bytes) return '0 b'
  const k = 1024
  const sizes = ['b', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
}

const _getStatusColor = (status) => {
  if (status === 'Pendiente')  return 'grey'
  if (status === 'Subiendo')   return 'primary'
  if (status === 'Completado') return 'success'
  if (status === 'Error')      return 'error'
  return 'grey'
}

const _limpiar = () => {
  cancelado.value       = false
  abortController.value = null
  tocIdArchivoActual.value = 0
  tocId.value                = 0
  nombreCarpeta.value        = ''
  plantillas.value           = []
  volumenes.value            = []
  plantillaSeleccionada.value = null
  plantillaBloqueada.value   = false
  volumenSeleccionado.value  = null
  archivosSeleccionados.value = []
  archivosTabla.value        = []
  progreso.value             = 0
  progressSize.value         = '0 b'
  subiendo.value             = false
  completado.value           = false
  errorSubida.value          = null
  tieneArchivoAV.value       = false
  configOCRActiva.value      = false
  idiomaSeleccionado.value   = 'es'
}

// ═══════════════════════════════════════════════════════════════
// EXPORT
// ═══════════════════════════════════════════════════════════════
export function useImportarMinimizado() {

  const puedeSubir = computed(() =>
    plantillaSeleccionada.value &&
    volumenSeleccionado.value   &&
    archivosTabla.value.length > 0 &&
    !subiendo.value
  )

  // ── Llamado desde treeNode ANTES de abrir el formulario ──────
  const abrirConConfig = ({ id, nombre, plantillasLista, volumenesLista, templateIdDetectado }) => {
    // Si ya está subiendo: solo restaurar el diálogo
    if (subiendo.value) {
      dialogVisible.value = true
      minimizado.value    = false
      return
    }

    _limpiar()
    tocId.value         = id
    nombreCarpeta.value = nombre || ''
    plantillas.value    = plantillasLista  || []
    volumenes.value     = volumenesLista   || []

    if (templateIdDetectado > 0) {
      const encontrada = plantillasLista.find(p => Number(p.codigo) === Number(templateIdDetectado))
      plantillaSeleccionada.value = encontrada ? encontrada.codigo : templateIdDetectado
      plantillaBloqueada.value    = true
    }

    dialogVisible.value = true
    minimizado.value    = false
  }

  // ── Visibilidad ──────────────────────────────────────────────
  const minimizar = () => {
    dialogVisible.value = false
    minimizado.value    = true
  }

  const restaurar = () => {
    dialogVisible.value = true
    minimizado.value    = false
  }

  const cerrar = async () => {
    if (subiendo.value) {
      const r = await Swal.fire({
        title: '¿Cancelar subida?',
        text: 'La subida está en progreso. ¿Deseas cancelarla?',
        icon: 'warning', iconColor: '#ce501b',
        showCancelButton: true,
        confirmButtonText: 'Sí, cancelar',
        cancelButtonText: 'No, continuar',
        confirmButtonColor: '#ce501b', cancelButtonColor: '#9B9B9B',
        customClass: { title: 'swal-title-custom', confirmButton: 'swal-btn-confirmar',
                       cancelButton: 'swal-btn-cancelar', container: 'swal-sobre-modal' }
      })
      if (!r.isConfirmed) return
      cancelado.value = true
      abortController.value?.abort()

      if (tocIdArchivoActual.value > 0) {
        await axios.delete(`/api/Upload/cancelar/${tocIdArchivoActual.value}`)
          .catch(() => {})  // silencioso — si falla no bloquea el cierre
        tocIdArchivoActual.value = 0
      }

      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'info',
        iconColor: '#ce501b',
        title: 'Importación cancelada',
        text: 'Los archivos que ya habían terminado la subida se conservan.',
        timer: 4000,
        showConfirmButton: false,
        customClass: { container: 'swal-sobre-modal' }
      })
    }
    dialogVisible.value = false
    minimizado.value    = false
    _limpiar()
  }

  // Limpia el ítem del menú sin abrir diálogo (ej: tras completarse y el usuario descarta)
  const descartar = async () => {
    if (subiendo.value) {
      const r = await Swal.fire({
        title: '¿Cancelar importación?',
        html: `La subida está en progreso (<strong>${progreso.value}%</strong> completado).<br>
              ¿Deseas cancelarla de todas formas?`,
        icon: 'warning',
        iconColor: '#ce501b',
        showCancelButton: true,
        confirmButtonText: 'Sí, cancelar subida',
        cancelButtonText: 'No, seguir subiendo',
        confirmButtonColor: '#ce501b',
        cancelButtonColor: '#9B9B9B',
        customClass: {
          title: 'swal-title-custom',
          confirmButton: 'swal-btn-confirmar',
          cancelButton: 'swal-btn-cancelar',
          container: 'swal-sobre-modal'
        }
      })
      if (!r.isConfirmed) return

      // ← Esto faltaba en tu versión
      cancelado.value = true
      abortController.value?.abort()

      if (tocIdArchivoActual.value > 0) {
        await axios.delete(`/api/Upload/cancelar/${tocIdArchivoActual.value}`)
          .catch(() => {})
        tocIdArchivoActual.value = 0
      }

      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'info',
        iconColor: '#ce501b',
        title: 'Importación cancelada',
        text: 'Los archivos que ya habían terminado la subida se conservan.',
        timer: 4000,
        showConfirmButton: false,
        customClass: { container: 'swal-sobre-modal' }
      })
    }

    dialogVisible.value      = false
    minimizado.value         = false
    pendingReloadTocId.value = null
    _limpiar()
  }

  // ── Actualizar tabla al seleccionar archivos ─────────────────
  const actualizarTablaArchivos = async (archivos) => {
    archivosSeleccionados.value = archivos
    archivosTabla.value = archivos.map(file => ({
      name: file.name, size: file.size, status: 'Pendiente', file
    }))

    const hayAV = archivos.some(f =>
      extensionesAV.includes(f.name.split('.').pop().toLowerCase())
    )
    tieneArchivoAV.value = hayAV

    if (hayAV) {
      try {
        const resp = await axios.get('/api/Configuracion/get')
        configOCRActiva.value = !!(resp.data?.success && resp.data.data?.estado === 1)
      } catch { configOCRActiva.value = false }
    } else {
      configOCRActiva.value = false
    }
  }

  const iniciarSubida = async () => {
    if (!puedeSubir.value) return

    // Validar formatos
    const invalidos = archivosTabla.value.filter(a => {
      const ext = a.name.split('.').pop().toLowerCase()
      return !formatosPermitidos.includes(ext)
    })
    if (invalidos.length > 0) {
      Swal.fire({
        icon: 'warning', iconColor: '#ce501b', title: 'Formato no permitido',
        html: `<p>Archivos no admitidos:</p>
              <ul style="text-align:left;max-height:120px;overflow-y:auto">
                ${invalidos.map(a => `<li>${a.name}</li>`).join('')}
              </ul>
              <p style="font-size:13px;color:#666;margin-top:8px">
                Formatos: mp4,mkv,webm,mov,avi,mp3,wav,flac,m4a,pdf,png,tiff,jpg,jpeg,zip
              </p>`,
        confirmButtonColor: '#ce501b', confirmButtonText: 'Entendido',
        customClass: { title: 'swal-title-custom', confirmButton: 'swal-btn-confirmar',
                      container: 'swal-sobre-modal' }
      })
      return
    }

    subiendo.value        = true
    cancelado.value       = false
    abortController.value = new AbortController()
    completado.value      = false
    errorSubida.value     = null
    progreso.value        = 0
    progressSize.value    = '0 b'

    const idUsuarioSesion = localStorage.getItem('idUsuario')
    const totalFiles      = archivosTabla.value.length
    let filesProcessed    = 0

    try {
      for (const archivoInfo of archivosTabla.value) {
        if (cancelado.value) break

        const file         = archivoInfo.file
        archivoInfo.status = 'Subiendo'

        const chunkSize   = 1024 * 1024
        const totalChunks = Math.ceil(file.size / chunkSize)
        const extFile     = file.name.split('.').pop().toLowerCase()
        const esAV        = extensionesAV.includes(extFile)
        let tocIdArchivo  = 0

        for (let ci = 0; ci < totalChunks; ci++) {
          if (cancelado.value) break

          const start = ci * chunkSize
          const end   = Math.min(start + chunkSize, file.size)

          const fd = new FormData()
          fd.append('file',       file.slice(start, end), file.name)
          fd.append('chunk',      ci.toString())
          fd.append('chunks',     totalChunks.toString())
          fd.append('fileName',   file.name)
          fd.append('parentId',   tocId.value.toString())
          fd.append('templateId', plantillaSeleccionada.value.toString())
          fd.append('volumenId',  volumenSeleccionado.value.volId.toString())
          fd.append('ruta',       volumenSeleccionado.value.pathTexto)
          fd.append('fileSize',   file.size.toString())

          if (esAV && configOCRActiva.value)
            fd.append('language', idiomaSeleccionado.value)

          if (ci > 0 && tocIdArchivo > 0)
            fd.append('tocIdArchivo', tocIdArchivo.toString())

          const resp = await axios.post(
            `/api/Upload/file?idUsuarioSesion=${idUsuarioSesion}`,
            fd,
            {
              signal: abortController.value.signal,
              headers: { 'Content-Type': 'multipart/form-data' },
              onUploadProgress: () => {
                const fileProgress  = (ci / totalChunks) * 100
                const totalProgress = ((filesProcessed + fileProgress / 100) / totalFiles) * 100
                progreso.value      = Math.round(totalProgress)
                progressSize.value  = _formatFileSize((file.size * (ci + 1)) / totalChunks)
              }
            }
          )

          if (!resp.data.success)
            throw new Error(resp.data.mensaje || 'Error al subir chunk')

          if (ci === 0 && resp.data.data?.tocId) {
            tocIdArchivo             = resp.data.data.tocId
            tocIdArchivoActual.value = resp.data.data.tocId
          }
        }

        // Solo marcar completado si no fue cancelado durante los chunks
        if (!cancelado.value) {
          archivoInfo.status = 'Completado'
          filesProcessed++
        }
        tocIdArchivo = 0
        tocIdArchivoActual.value = 0
      }

      // ✅ Éxito — solo si terminó sin cancelación
      if (!cancelado.value) {
        pendingReloadTocId.value = tocId.value
        progreso.value           = 100
        completado.value         = true
        subiendo.value           = false
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          iconColor: '#2e7d32',
          title: 'Carga completada',
          text: `${totalFiles} archivo(s) importado(s) correctamente`,
          timer: 3500,
          showConfirmButton: false,
          customClass: { container: 'swal-sobre-modal' }
        })
        dialogVisible.value = false
        minimizado.value    = false
        _limpiar()
      }

    } catch (err) {
      // Verificación nativa — no depende de la instancia de axios
      if (err.name === 'CanceledError' || err.name === 'AbortError' || cancelado.value) {
        subiendo.value = false
        return
      }

      subiendo.value    = false
      errorSubida.value = err.response?.data?.mensaje || err.message || 'Error al subir'
      Swal.fire({
        icon: 'error', iconColor: '#ce501b', title: 'Error',
        text: errorSubida.value,
        customClass: { title: 'swal-title-custom', container: 'swal-sobre-modal' }
      })
    }
  }

  return {
    // state (reactivo, compartido)
    dialogVisible, minimizado,
    tocId, nombreCarpeta,
    plantillas, volumenes,
    plantillaSeleccionada, volumenSeleccionado, plantillaBloqueada,
    archivosSeleccionados, archivosTabla,
    subiendo, progreso, progressSize, completado, errorSubida,
    tieneArchivoAV, configOCRActiva, idiomaSeleccionado,
    pendingReloadTocId,
    puedeSubir,
    // helpers
    formatFileSize: _formatFileSize,
    getStatusColor: _getStatusColor,
    // actions
    abrirConConfig, minimizar, restaurar, cerrar, descartar,
    actualizarTablaArchivos, iniciarSubida,
  }
}